import { Box, IconButton, InputAdornment, List, ListItemButton, Popover, Stack, TextField, Tooltip } from '@mui/material'
import { MagnifyingGlassIcon } from '@phosphor-icons/react'
import { createAccountPresentation, DisplayUserLink, tokens, User, UserBubble } from 'ComponentLibrary'
import { useState } from 'react'
import { UsersPreview } from 'ComponentLibrary/src/molecules/UserBubble/UserBubbleList'
import { groupBy } from 'lodash'
import { Typography } from 'ComponentLibrary/src/piazza'
import { assembleUserSearchText, TaskAssignee } from 'ComponentLibrary/src/common/users'
import { type UserLinkCopy } from 'ComponentLibrary/src/piazza/text/UserLink/UserLink'

type TaskAssigneePopoverProps = {
    assignee: TaskAssignee | null
    options: User[]
    onChange: (selectedAssignee: TaskAssignee | null) => void
    copy?: Partial<TaskAssigneePopoverCopy>
    userLinkCopy?: Partial<UserLinkCopy>
}

export type TaskAssigneePopoverCopy = {
    searchPlaceholder: string
}

const DEFAULT_TASK_ASSIGNEE_POPOVER_COPY: TaskAssigneePopoverCopy = {
    searchPlaceholder: 'Nutzer durchsuchen',
}

export function TaskAssigneePopover({ assignee, onChange, options, copy, userLinkCopy }: TaskAssigneePopoverProps) {
    const resolvedCopy = { ...DEFAULT_TASK_ASSIGNEE_POPOVER_COPY, ...copy }
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation()
        setAnchorEl(event.currentTarget)
    }

    const handleClose = () => {
        setAnchorEl(null)
    }

    const onChangeAssignee = (option: TaskAssignee | null) => {
        handleClose()
        onChange(option)
    }

    const taskAssigneeUser = options.find((user) => user.id === assignee?.id)

    return (
        <Box>
            <Tooltip
                title={
                    taskAssigneeUser ? (
                        <UsersPreview presentations={[createAccountPresentation(taskAssigneeUser)]} userLinkCopy={userLinkCopy} />
                    ) : undefined
                }
                componentsProps={{ tooltip: { sx: { ...tokens.Popup, backgroundColor: tokens.ColorBackgroundLight } } }}
            >
                <IconButton
                    onClick={handleClick}
                    sx={{
                        borderRadius: tokens.SizingCornerRound,
                        '&:hover': {
                            backgroundColor: tokens.ColorSurfaceHover,
                        },
                        '&:active': {
                            backgroundColor: tokens.ColorSurfacePressed,
                        },
                    }}
                >
                    <UserBubble
                        size={tokens.SizingIconM}
                        profileImageUrl={taskAssigneeUser?.profile_thumbnail_image_url}
                        profileFallbackImageUrl={taskAssigneeUser?.profile_image_url}
                        defaultIconCustomColors={
                            !assignee ? { color: tokens.ColorIconDisabled, bgColor: tokens.ColorSurfaceDisabled } : undefined
                        }
                    />
                </IconButton>
            </Tooltip>
            <Popover
                open={!!anchorEl}
                anchorEl={anchorEl}
                onClose={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation()
                    handleClose()
                }}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                slotProps={{ paper: { sx: { borderRadius: tokens.SizingCornerS, ...tokens.Flyout } } }}
            >
                <CustomUserSelectWithSearch
                    options={options.filter((user) => user.is_active !== false)}
                    onChange={onChangeAssignee}
                    selectedAssignee={assignee}
                    copy={resolvedCopy}
                    userLinkCopy={userLinkCopy}
                />
            </Popover>
        </Box>
    )
}

type CustomUserSelectWithSearchProps = {
    options: User[]
    onChange: (selectedAssignee: TaskAssignee | null) => void
    selectedAssignee: Pick<TaskAssignee, 'id'> | null
    copy: TaskAssigneePopoverCopy
    userLinkCopy?: Partial<UserLinkCopy>
}

function CustomUserSelectWithSearch({ options, onChange, selectedAssignee, copy, userLinkCopy }: CustomUserSelectWithSearchProps) {
    const [searchTerm, setSearchTerm] = useState('')

    const optionsToRender = searchTerm !== '' ? filterOptions(searchTerm, options) : options
    const optionsGroupedByCustomer = groupBy(optionsToRender, 'organization_name')

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' && optionsToRender.length === 1) {
            event.preventDefault()
            onChange(optionsToRender[0])
        }
    }

    return (
        <Stack width="436px" maxHeight="600px" padding={`${tokens.SizingSpacing100} 0`}>
            <Box padding={`${tokens.SizingSpacing050} ${tokens.SizingSpacing150}`}>
                <TextField
                    onClick={(e) => e.stopPropagation()} // Prevent triggering other click events, for example ones below the popover
                    autoFocus
                    fullWidth
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={copy.searchPlaceholder}
                    variant="outlined"
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: tokens.SizingCornerS,
                            padding: `0 ${tokens.SizingSpacing150}`,
                            height: '46px',
                            '&.Mui-focused fieldset': {
                                border: `1px solid ${tokens.ColorBorderFocused}`,
                            },
                        },
                    }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <MagnifyingGlassIcon size={tokens.SizingIconM} color={tokens.ColorIconSubtle} />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>
            <List sx={{ paddingTop: tokens.SizingSpacing100, paddingBottom: 0 }}>
                <UserListItemButton selected={selectedAssignee === null} onChange={onChange} option={null} userLinkCopy={userLinkCopy} />
                {Object.entries(optionsGroupedByCustomer).map(([groupName, groupedOptionsOfCustomer]) => (
                    <UserListGroup
                        key={groupName}
                        groupName={groupName}
                        selectedAssignee={selectedAssignee}
                        onChange={onChange}
                        optionsToRender={groupedOptionsOfCustomer}
                        userLinkCopy={userLinkCopy}
                    />
                ))}
            </List>
        </Stack>
    )
}

type UserListGroupProps = {
    groupName: string
    optionsToRender: User[]
    selectedAssignee: Pick<TaskAssignee, 'id'> | null
    onChange: (selectedAssignee: TaskAssignee | null) => void
    userLinkCopy?: Partial<UserLinkCopy>
}

function UserListGroup({ groupName, optionsToRender, selectedAssignee, onChange, userLinkCopy }: UserListGroupProps) {
    return (
        <>
            <Stack
                padding={`${tokens.SizingSpacing050} ${tokens.SizingSpacing150}`}
                direction="row"
                style={{ height: tokens.SizingSpacing600 }}
                alignItems="center"
            >
                <Typography variant="headline4" color={tokens.ColorTextSubtle}>
                    {groupName}
                </Typography>
            </Stack>
            {optionsToRender.map((option) => (
                <UserListItemButton
                    key={option.id}
                    selected={selectedAssignee?.id === option.id}
                    option={option}
                    onChange={onChange}
                    userLinkCopy={userLinkCopy}
                />
            ))}
        </>
    )
}

type UserListItemButtonProps = Pick<CustomUserSelectWithSearchProps, 'onChange'> & {
    selected: boolean
    option: User | null
    userLinkCopy?: Partial<UserLinkCopy>
}

function UserListItemButton({ selected, onChange, option, userLinkCopy }: UserListItemButtonProps) {
    return (
        <ListItemButton
            selected={selected}
            sx={{
                padding: `${tokens.SizingSpacing050} ${tokens.SizingSpacing150}`,
                '&:hover': {
                    backgroundColor: `${tokens.ColorSurfaceHover}`,
                    cursor: 'pointer',
                },
                '&.Mui-selected': {
                    backgroundColor: tokens.ColorSurfaceSelectedDefault,
                },
            }}
            onClick={(event) => {
                event.stopPropagation()
                onChange(option)
            }}
        >
            <DisplayUserLink user={option ?? undefined} size="medium" showProfileOnHover={false} copy={userLinkCopy} />
        </ListItemButton>
    )
}

function filterOptions(searchTerm: string, options: CustomUserSelectWithSearchProps['options']) {
    const lowerCaseSearchTerm = searchTerm.toLowerCase()
    return options.filter((option) => assembleUserSearchText(option).toLowerCase().includes(lowerCaseSearchTerm))
}
